import React, { useState, useEffect } from 'react';
import { 
  ArrowsUpDownIcon, 
  TrashIcon, 
  EyeIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

export interface ReceiptLayoutElement {
  id: string;
  type: 'logo' | 'header' | 'order_number' | 'token' | 'date' | 'customer_info' | 'divider' | 'items' | 'subtotal' | 'discount' | 'tax' | 'service_charge' | 'total' | 'payment_info' | 'notes' | 'footer' | 'custom_text';
  label: string;
  enabled: boolean;
  order: number;
  align?: 'left' | 'center' | 'right';
  bold?: boolean;
  fontSize?: 'small' | 'normal' | 'large';
  customText?: string;
}

interface ReceiptLayoutDesignerProps {
  layout: ReceiptLayoutElement[];
  onLayoutChange: (layout: ReceiptLayoutElement[]) => void;
  onClose?: () => void;
}

const defaultLayout: ReceiptLayoutElement[] = [
  { id: '1', type: 'header', label: 'Header (Order Receipt)', enabled: true, order: 1, align: 'center', bold: true, fontSize: 'large' },
  { id: '2', type: 'order_number', label: 'Order Number', enabled: true, order: 2, align: 'center', bold: true },
  { id: '3', type: 'token', label: 'Token Number', enabled: true, order: 3, align: 'center', bold: true },
  { id: '4', type: 'date', label: 'Date/Time', enabled: true, order: 4, align: 'center', bold: false },
  { id: '5', type: 'divider', label: 'Divider', enabled: true, order: 5 },
  { id: '6', type: 'customer_info', label: 'Customer Info', enabled: true, order: 6, align: 'left', bold: true },
  { id: '7', type: 'divider', label: 'Divider', enabled: true, order: 7 },
  { id: '8', type: 'items', label: 'Order Items', enabled: true, order: 8 },
  { id: '9', type: 'divider', label: 'Divider', enabled: true, order: 9 },
  { id: '10', type: 'subtotal', label: 'Subtotal', enabled: true, order: 10, align: 'left' },
  { id: '11', type: 'discount', label: 'Discount', enabled: true, order: 11, align: 'left' },
  { id: '12', type: 'tax', label: 'Tax', enabled: true, order: 12, align: 'left' },
  { id: '13', type: 'service_charge', label: 'Service Charge', enabled: true, order: 13, align: 'left' },
  { id: '14', type: 'total', label: 'Total', enabled: true, order: 14, align: 'left', bold: true, fontSize: 'large' },
  { id: '15', type: 'payment_info', label: 'Payment Info', enabled: true, order: 15, align: 'left' },
  { id: '16', type: 'notes', label: 'Order Notes', enabled: true, order: 16, align: 'left' },
  { id: '17', type: 'footer', label: 'Footer (Thank you)', enabled: true, order: 17, align: 'center', bold: true },
];

const ReceiptLayoutDesigner: React.FC<ReceiptLayoutDesignerProps> = ({
  layout,
  onLayoutChange,
  onClose
}) => {
  const [elements, setElements] = useState<ReceiptLayoutElement[]>(layout.length > 0 ? layout : defaultLayout);
  const [draggedElement, setDraggedElement] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [editingElement, setEditingElement] = useState<ReceiptLayoutElement | null>(null);

  useEffect(() => {
    setElements(layout.length > 0 ? layout : defaultLayout);
  }, [layout]);

  const handleDragStart = (e: React.DragEvent, elementId: string) => {
    setDraggedElement(elementId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetElementId: string) => {
    e.preventDefault();
    if (!draggedElement || draggedElement === targetElementId) return;

    const items = [...elements];
    const draggedIndex = items.findIndex(item => item.id === draggedElement);
    const targetIndex = items.findIndex(item => item.id === targetElementId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const [reorderedItem] = items.splice(draggedIndex, 1);
    items.splice(targetIndex, 0, reorderedItem);

    // Update order numbers
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index + 1
    }));

    setElements(updatedItems);
    onLayoutChange(updatedItems);
    setDraggedElement(null);
  };

  const handleDragEnd = () => {
    setDraggedElement(null);
  };

  const toggleElementEnabled = (elementId: string) => {
    const updated = elements.map(el => 
      el.id === elementId ? { ...el, enabled: !el.enabled } : el
    );
    setElements(updated);
    onLayoutChange(updated);
  };

  const updateElementProperty = (elementId: string, property: keyof ReceiptLayoutElement, value: any) => {
    const updated = elements.map(el => 
      el.id === elementId ? { ...el, [property]: value } : el
    );
    setElements(updated);
    onLayoutChange(updated);
  };

  const deleteElement = (elementId: string) => {
    const updated = elements.filter(el => el.id !== elementId);
    // Reorder remaining elements
    const reordered = updated.map((el, index) => ({ ...el, order: index + 1 }));
    setElements(reordered);
    onLayoutChange(reordered);
  };

  const addCustomText = () => {
    const newElement: ReceiptLayoutElement = {
      id: `custom-${Date.now()}`,
      type: 'custom_text',
      label: 'Custom Text',
      enabled: true,
      order: elements.length + 1,
      align: 'left',
      bold: false,
      fontSize: 'normal',
      customText: 'Enter custom text here'
    };
    const updated = [...elements, newElement];
    setElements(updated);
    onLayoutChange(updated);
    setEditingElement(newElement);
  };

  const getElementIcon = (type: string) => {
    switch (type) {
      case 'logo': return '🖼️';
      case 'header': return '📋';
      case 'order_number': return '#️⃣';
      case 'token': return '🎫';
      case 'date': return '📅';
      case 'customer_info': return '👤';
      case 'divider': return '─';
      case 'items': return '📦';
      case 'subtotal': return '💰';
      case 'discount': return '🎁';
      case 'tax': return '📊';
      case 'service_charge': return '💳';
      case 'total': return '💵';
      case 'payment_info': return '💳';
      case 'notes': return '📝';
      case 'footer': return '🙏';
      case 'custom_text': return '✏️';
      default: return '•';
    }
  };

  const renderPreview = () => {
    const enabledElements = elements.filter(el => el.enabled).sort((a, b) => a.order - b.order);
    
    return (
      <div className="bg-white p-6 rounded-lg border-2 border-gray-300" style={{ width: '80mm', maxWidth: '80mm', fontFamily: 'Calibri, Helvetica, Arial, sans-serif' }}>
        {enabledElements.map((element) => {
          const style: React.CSSProperties = {
            textAlign: element.align || 'left',
            fontWeight: element.bold ? 'bold' : 'normal',
            fontSize: element.fontSize === 'large' ? '18px' : element.fontSize === 'small' ? '12px' : '14px',
            marginBottom: '8px'
          };

          switch (element.type) {
            case 'logo':
              return (
                <div key={element.id} style={{ textAlign: element.align || 'center', marginBottom: '8px' }}>
                  <div style={{ fontSize: '12px', color: '#666' }}>[Logo Placeholder]</div>
                </div>
              );
            case 'header':
              return <div key={element.id} style={style}>ORDER RECEIPT</div>;
            case 'order_number':
              return <div key={element.id} style={style}>Order #12345</div>;
            case 'token':
              return <div key={element.id} style={style}>Token #42</div>;
            case 'date':
              return <div key={element.id} style={style}>Jan 15, 2024, 2:30 PM</div>;
            case 'divider':
              return <div key={element.id} style={{ borderTop: '1px dashed #000', margin: '8px 0' }}></div>;
            case 'customer_info':
              return <div key={element.id} style={style}><div>John Doe</div><div>+1234567890</div><div>Takeaway</div></div>;
            case 'items':
              return (
                <div key={element.id}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Items</div>
                  <div style={{ fontSize: '13px', marginBottom: '4px' }}>2x Chicken Biryani</div>
                  <div style={{ fontSize: '13px', marginLeft: '10px' }}>Variant: Large (+5.00)</div>
                  <div style={{ fontSize: '13px', marginLeft: '10px' }}>Addons: Extra Spice (+2.00)</div>
                  <div style={{ textAlign: 'right', fontSize: '14px', fontWeight: 'bold' }}>SAR 25.00</div>
                </div>
              );
            case 'subtotal':
              return <div key={element.id} style={style}>Subtotal: SAR 100.00</div>;
            case 'discount':
              return <div key={element.id} style={{ ...style, color: '#008000' }}>Discount: -SAR 10.00</div>;
            case 'tax':
              return <div key={element.id} style={style}>Tax: SAR 15.00</div>;
            case 'service_charge':
              return <div key={element.id} style={style}>Service Charge: SAR 5.00</div>;
            case 'total':
              return <div key={element.id} style={{ ...style, borderTop: '2px solid #000', paddingTop: '4px', marginTop: '4px' }}>Total: SAR 110.00</div>;
            case 'payment_info':
              return <div key={element.id} style={style}><div>Payment: Cash</div><div>Paid: SAR 110.00</div><div>Status: Paid</div></div>;
            case 'notes':
              return <div key={element.id} style={style}>Notes: No onions please</div>;
            case 'footer':
              return <div key={element.id} style={style}>Thank you for your order!</div>;
            case 'custom_text':
              return <div key={element.id} style={style}>{element.customText || 'Custom Text'}</div>;
            default:
              return null;
          }
        })}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Receipt Layout Designer</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <EyeIcon className="h-5 w-5 mr-2" />
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {showPreview ? (
            <div className="flex flex-col items-center">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Preview</h3>
              {renderPreview()}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Drag elements to reorder. Toggle to enable/disable. Click to edit properties.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const logoExists = elements.some(el => el.type === 'logo');
                      if (!logoExists) {
                        const newElement: ReceiptLayoutElement = {
                          id: `logo-${Date.now()}`,
                          type: 'logo',
                          label: 'Logo',
                          enabled: true,
                          order: 0,
                          align: 'center'
                        };
                        const updated = [newElement, ...elements.map(el => ({ ...el, order: el.order + 1 }))];
                        setElements(updated);
                        onLayoutChange(updated);
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    + Add Logo
                  </button>
                  <button
                    onClick={addCustomText}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                  >
                    + Add Custom Text
                  </button>
                </div>
              </div>

              {elements.map((element) => (
                <div
                  key={element.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, element.id)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, element.id)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                    draggedElement === element.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50'
                  } ${!element.enabled ? 'opacity-50' : ''}`}
                >
                  <div className="flex-shrink-0 cursor-move">
                    <ArrowsUpDownIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  
                  <div className="flex-shrink-0 text-2xl">
                    {getElementIcon(element.type)}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-white">{element.label}</span>
                      {element.type === 'custom_text' && (
                        <span className="text-xs text-gray-500">({element.customText})</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {element.align && `Align: ${element.align}`}
                      {element.bold && ' • Bold'}
                      {element.fontSize && ` • ${element.fontSize}`}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setEditingElement(element)}
                      className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => toggleElementEnabled(element.id)}
                      className={`px-3 py-1 text-sm rounded ${
                        element.enabled
                          ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {element.enabled ? 'Enabled' : 'Disabled'}
                    </button>
                    {element.type !== 'items' && element.type !== 'logo' && (
                      <button
                        onClick={() => deleteElement(element.id)}
                        className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Edit Modal */}
        {editingElement && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Edit Element</h3>
              
              <div className="space-y-4">
                {editingElement.type === 'custom_text' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Custom Text
                    </label>
                    <input
                      type="text"
                      value={editingElement.customText || ''}
                      onChange={(e) => {
                        const updated = { ...editingElement, customText: e.target.value };
                        setEditingElement(updated);
                        updateElementProperty(editingElement.id, 'customText', e.target.value);
                      }}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                )}

                {['logo', 'header', 'order_number', 'token', 'date', 'customer_info', 'subtotal', 'discount', 'tax', 'service_charge', 'total', 'payment_info', 'notes', 'footer', 'custom_text'].includes(editingElement.type) && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Alignment
                      </label>
                      <select
                        value={editingElement.align || 'left'}
                        onChange={(e) => {
                          const updated = { ...editingElement, align: e.target.value as 'left' | 'center' | 'right' };
                          setEditingElement(updated);
                          updateElementProperty(editingElement.id, 'align', e.target.value);
                        }}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </select>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="bold"
                        checked={editingElement.bold || false}
                        onChange={(e) => {
                          const updated = { ...editingElement, bold: e.target.checked };
                          setEditingElement(updated);
                          updateElementProperty(editingElement.id, 'bold', e.target.checked);
                        }}
                        className="h-4 w-4 text-blue-600"
                      />
                      <label htmlFor="bold" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Bold
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Font Size
                      </label>
                      <select
                        value={editingElement.fontSize || 'normal'}
                        onChange={(e) => {
                          const updated = { ...editingElement, fontSize: e.target.value as 'small' | 'normal' | 'large' };
                          setEditingElement(updated);
                          updateElementProperty(editingElement.id, 'fontSize', e.target.value);
                        }}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="small">Small</option>
                        <option value="normal">Normal</option>
                        <option value="large">Large</option>
                      </select>
                    </div>
                  </>
                )}
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setEditingElement(null)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceiptLayoutDesigner;

